import {h, Component} from 'preact'
import Phrase from '../../libs/translationsUtils/phrase'

import Popup from '../Popup/index'
import Tabs from '../Tabs/index'
import UserAvatar from '../User/UserAvatar'
import UserName from '../User/UserName'
import ReputationBar from '../User/ReputationBar'

import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import globals from '../../utils/globals'

interface UserStatisticProps {
  label: string,
  value: number
}

export const UserStatistic = ({label, value}: UserStatisticProps) => (
  <div>
    <div>{value}</div>
    <div class='text--meta'>
      <Phrase p={label} count={{value: '' + value}} />
    </div>
  </div>
)

const SendMessageIcon = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
    <path d='M2 0C.898 0 0 .898 0 2v12c0 1.102.898 2 2 2h6.594c-.102-.3-.094-.7-.094-1 0-.3-.008-.7.094-1H2V2h16v7.188c.8.398 1.398.925 2 1.624V2c0-1.102-.898-2-2-2H2zm1 2.594v2L10 9l7-4.406v-2L10 7 3 2.594zM15 10c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm-1 2h2v2h2v2h-2v2h-2v-2h-2v-2h2v-2z' fill-rule='nonzero' fill='#000' />
  </svg>
)

interface User {
  numberOfTopics: number,
  numberOfReplies: number,
  numberOfSolved: number,
  showBadges: boolean,
  showReputation: boolean,
  name: string,
  userTitle: string,
  id: number,
  badges: Array<{
    title: string,
    url: string
  }>,
  username: string,
  profileFields: Array<{
    value: string,
    values: Array<string>,
    name: string
  }>
}

interface UserStatsProp {
  user: User
}

export const UserStats = ({user}: UserStatsProp) => (
  <div style={{
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  }}>
    {[
      {value: user.numberOfTopics, label: 'Topic|Topics'},
      {value: user.numberOfReplies, label: 'Reply|Replies'},
      {value: user.numberOfSolved, label: 'Solved'}
    ].map(statistic => <UserStatistic {...statistic} />)}
  </div>
)

interface ExtraUserInfoProps extends UserStatsProp {
  disableTabs: boolean,
  user: User
}

const ExtraUserInfo = ({user, disableTabs}: ExtraUserInfoProps) => (
  user.showBadges
    ? <Tabs titles={[<Phrase p='Badges' />, <Phrase p='About' />]} style={{padding: '20px 0'}} disableClick={disableTabs}>
      <BadgeList user={user} />
      <ProfileFields profileFields={user.profileFields} />
    </Tabs>
    : <div style={{paddingTop: '20px'}} >
      <ProfileFields profileFields={user.profileFields} />
    </div>
)

interface UserProfileProps {
  user: User,
  profileRef: (ref: HTMLElement | undefined) => void,
  currentUserId: number,
  disableTabs: boolean,
  isPMActive: boolean
}

export const UserProfile = ({profileRef, user, currentUserId, disableTabs, isPMActive}: UserProfileProps) => (
  <div>
    <div
      ref={el => profileRef(el)}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'top',
        width: '100%'
      }}>
      <div>
        <UserAvatar
          disablePopup
          showRank
          user={user}
          style={{
            marginBottom: '10px',
            width: '60px',
            height: '60px'
          }}
        />
        {user.showReputation ? <ReputationBar user={user} /> : null}
      </div>

      <div style={{
        flexGrow: 1,
        marginLeft: '20px'
      }}>
        <UserName user={user} />
        <div class='text--meta' style={{
          marginBottom: '10px'
        }}>{user.userTitle}</div>
        <UserStats user={user} />
      </div>

      {
        isPMActive && currentUserId && currentUserId !== user.id ? (
          <a href={globals('application') !== 'control' ? `/inbox/conversation?with=${user.id}` : `/privateMessages/overview#conversation/${user.id}`}>
            <SendMessageIcon style={{position: 'absolute', top: '20px', right: '20px'}} />
          </a>
        ) : null
      }

    </div>
    <ExtraUserInfo user={user} disableTabs={disableTabs} />
  </div>
)

interface BadgeListProps {
  user: User
}

const BadgeList = ({user}: BadgeListProps) => {
  const {badges, name} = user
  return badges.length
    ? <div style={{
      display: 'flex',
      width: '100%',
      alignContent: 'space-around',
      flexWrap: 'wrap'
    }}>
      {badges.map(({title, url}, i) => (
        <div style={{
          float: 'left',
          display: 'flex',
          alignItems: 'center',
          width: '50%',
          paddingRight: i % 2 === 1 ? '5px' : 0,
          paddingTop: i >= 2 ? '8px' : 0
        }}>
          <img title={title} src={url} style={{
            width: '32px',
            marginRight: '8px'
          }} />
          <div style={{
            maxWidth: 'calc(100% - 40px)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {title}
          </div>
        </div>
      ))}
    </div>
    : <Phrase p='member.profile.no.badges' m='Common' count={{'username': name}} />
}

const ProfileFields = ({profileFields}: {profileFields: User['profileFields']}) => {
  if (!profileFields) return null

  return <table style={{
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed'
  }}>
    <tbody>
      {
        profileFields
          .filter(({value, values}) => value || (values && values.length))
          .map(({name, value, values}) => (
            <tr style={{wordWrap: 'break-word'}}>
              <td style={{width: '30%', verticalAlign: 'top'}}>{name}</td>
              <td style={{textAlign: 'right', width: '70%', paddingLeft: '10px', verticalAlign: 'top'}}>{value || values.join(', ')}</td>
            </tr>
          ))
      }
    </tbody>
  </table>
}

const loadMemberProfile = (memberId: number, cb: (u: User) => void, liveData: boolean) => {
  if (liveData) {
    getWithInMemoryCache.clearCache()
  }
  getWithInMemoryCache('/member/memberProfile', {memberId}).then(cb)
}

interface UserProfileApiWrapperProps {
  liveData: boolean,
  memberId: number,
  isPMActive: boolean,
}

interface State {
  user: User
}


export class UserProfileApiWrapper extends Component<UserProfileApiWrapperProps, State> {
  ref: HTMLElement | undefined
  height: number | undefined
  width: number | undefined

  componentDidMount() {
    loadMemberProfile(this.props.memberId, user => this.setState({user}), this.props.liveData)
  }

  componentWillReceiveProps(props: UserProfileApiWrapperProps) {
    loadMemberProfile(props.memberId, user => this.setState({user}), props.liveData)
  }

  render(props: UserProfileApiWrapperProps, state: State) {
    const {isPMActive, liveData} = props
    const {user} = state
    const currentUserId = parseInt(window.inSidedData.user.userid)

    return user
      ? <UserProfile
        profileRef={el => this.ref = el}
        user={user}
        currentUserId={currentUserId}
        isPMActive={isPMActive}
        disableTabs={liveData}
      />
      : <div class='generic-loader' ref={el => this.ref = el} style={{
        color: '#ccc',
        marginTop: '-15px',
        marginBottom: '15px'
      }} />
  }
}

interface Props {
  hoverControl: HTMLElement,
  parentEl: HTMLElement,
  liveData: boolean,
  memberId: number
}

export default class ProfilePopup extends Component<Props, State> {
  render(props: Props) {
    const isPMActive = (globals('activeModules') || []).indexOf('privateMessage') !== -1
    return (
      <Popup hoverControl={props.hoverControl ? props.hoverControl : props.parentEl} size={{width: 400, height: 60}} triggerOnClick={props.liveData}>
        <UserProfileApiWrapper
          isPMActive={isPMActive}
          memberId={props.memberId}
          liveData={props.liveData}
        />
      </Popup>
    )
  }
}
